import isNil from 'lodash/isNil';

import { danger } from 'redux-store/reducers/common-reducer';

import { __t, applyTranslations } from 'utils/language';
import { API, destroy, get, post } from 'utils/api';
import { logError } from 'utils/log';
import {
  determineModalType,
  handleRemoveModalStorage,
  handleSetModalStorage
} from '../utils/facebook-button';

applyTranslations(require('../facebook-button.i18n.json'));

import type {
  Dispatch,
  GetState,
  ThunkAction
} from 'types';
import type {
  FacebookButtonAction,
  FacebookButtonAuthResponse,
  FacebookButtonBackendResponse,
  FacebookButtonModalType
} from '../types';

const { INFLUENCERS } = API.END_POINTS;
const MSG_KEY = 'facebook_button';

export const loginAction = (
  instagramUserName: string | null
): FacebookButtonAction => ({
  type: 'facebook-button-login',
  payload: {
    instagramUserName
  }
});

export const expireTokenAction = (): FacebookButtonAction => ({
  type: 'facebook-button-expire-token'
});

export const logoutAction = (): FacebookButtonAction => ({
  type: 'facebook-button-logout'
});

export const saveResponseAction = (
  authResponse: FacebookButtonAuthResponse
): FacebookButtonAction => ({
  type: 'facebook-button-save-response',
  payload: {
    authResponse
  }
});

export const updateModalTypeAction = (
  modalType: FacebookButtonModalType
): FacebookButtonAction => ({
  type: 'facebook-button-update-modal-type',
  payload: {
    modalType
  }
});

export const setErrorAction = (
  error: string | null
): FacebookButtonAction => ({
  type: 'facebook-button-dialog-error',
  payload: {
    error
  }
});

export const flushState = (): FacebookButtonAction => ({
  type: 'facebook-button-flush-state'
});

export const updateModalType = (
  creatorProfileId: number
): ThunkAction => (
  async (dispatch: Dispatch, getState: GetState) => {
    try {
      const isFBLoginActive = getState().facebookButton.isFBLoginActive;
      const isFBLoginExpired = getState().facebookButton.isFBLoginExpired;
      const modalType = await determineModalType(creatorProfileId, isFBLoginActive, isFBLoginExpired);

      dispatch(updateModalTypeAction(modalType));
    } catch (error) {
      logError(error);
      dispatch(danger(__t(`${MSG_KEY}.errors.generic`)));
    }
  }
);

export const updateLoginState = (
  creatorProfileId: number
): ThunkAction => (
  async (dispatch: Dispatch) => {
    try {
      const url = INFLUENCERS.facebookTokenByCreatorId(creatorProfileId);
      const response = await get(url);
      const data: FacebookButtonBackendResponse = response?.data ?? {};

      const now = new Date();
      const expirationTime = !isNil(data.expiresAt)
        ? new Date(data.expiresAt)
        : new Date();

      if (!data.facebookCode) {
        dispatch(logoutAction());
      } else if (expirationTime.getTime() < now.getTime()) {
        dispatch(logoutAction());
        dispatch(expireTokenAction());
      } else {
        dispatch(loginAction(data.instagramUserName));
      }

      dispatch(updateModalType(creatorProfileId));
    } catch (error) {
      const details = error?.response?.data?.detail;
      const message = details ?? __t(`${MSG_KEY}.errors.login_check`);

      logError(error);

      dispatch(danger(message));
    }
  }
);

export const handleLoginResponse = (
  authResponse: FacebookButtonAuthResponse,
  creatorProfileId: number
): ThunkAction => (
  async (dispatch: Dispatch) => {
    const isValidCreator = !isNil(creatorProfileId) && creatorProfileId !== -1;

    try {
      const url = isValidCreator
        ? INFLUENCERS.facebookTokenByCreatorId(creatorProfileId)
        : INFLUENCERS.facebookInfo();

      const postData = {
        facebook_code: authResponse.accessToken,
        expirationTime: authResponse.data_access_expiration_time,
        userId: authResponse.userID
      };

      const response = await post(url, { data: postData }, { suppressGlobalNotification: true });
      const responseData: FacebookButtonBackendResponse = response?.data ?? {};

      dispatch(loginAction(responseData.instagramUserName));
      dispatch(saveResponseAction(authResponse));

      if (isValidCreator) {
        handleRemoveModalStorage(creatorProfileId);
      }

      return true;
    } catch (error) {
      const details = error?.response?.data?.detail;
      const message = details ?? __t(`${MSG_KEY}.errors.handle_login`);

      logError(error);

      dispatch(danger(message));
      dispatch(setErrorAction(message));
      dispatch(logoutAction());

      return null;
    }
  }
);

export const handleLogoutResponse = (
  creatorProfileId: number
): ThunkAction => (
  async (dispatch: Dispatch) => {
    const isValidCreator = !isNil(creatorProfileId) && creatorProfileId !== -1;

    if (!isValidCreator) {
      dispatch(flushState());
      return;
    }

    try {
      const url = INFLUENCERS.facebookTokenByCreatorId(creatorProfileId);

      await destroy(url);

      await handleSetModalStorage(creatorProfileId, true);

      dispatch(logoutAction());
    } catch (error) {
      const details = error?.response?.data?.detail;
      const message = details ?? __t(`${MSG_KEY}.errors.handle_logout`);

      logError(error);

      dispatch(danger(message));
    }
  }
);
